import React, { useState, useRef, useEffect } from 'react';
import { Card, Input, Button, Avatar, Typography, Spin, Row, Col } from 'antd';
import { SendOutlined, RobotOutlined, UserOutlined, DeleteOutlined } from '@ant-design/icons';
import * as FaIcons from 'react-icons/fa';
import Sidebar from '../components/sidebar';
import axios from 'axios';
import './ChatIA.css';

const { TextArea } = Input;
const { Text, Title } = Typography;

const ChatIA = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "¡Hola! 👋 Soy tu asistente virtual de CIRP. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      const messagesContainer = messagesEndRef.current.closest('.chat-ia-messages');
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
      }
    }
  };

  useEffect(() => {
    if (!isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, isUserScrolling]);



  // Cargar información del usuario
  useEffect(() => {
    const loginUserJSON = window.localStorage.getItem("loginUser");
    if (loginUserJSON) {
      const user = JSON.parse(loginUserJSON);
      setUserData(user);
    }
  }, []);

  // Detectar scroll manual del usuario
  const handleScroll = (e) => {
    const container = e.target;
    const isAtBottom = container.scrollHeight - container.scrollTop === container.clientHeight;
    
    if (isAtBottom) {
      setIsUserScrolling(false);
    } else {
      setIsUserScrolling(true);
    }
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setIsUserScrolling(false); // Forzar scroll al final cuando se envía mensaje

    // Scroll inmediatamente después de agregar el mensaje del usuario
    setTimeout(() => {
      scrollToBottom();
    }, 100);

    try {
      // Simular respuesta del chatbot
      const botResponse = await generateBotResponse(inputMessage);
      
      setTimeout(() => {
        const botMessage = {
          id: Date.now() + 1,
          text: botResponse,
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, botMessage]);
        setIsLoading(false);
        
        // Scroll después de la respuesta del bot
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }, 1000);
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      const errorMessage = {
        id: Date.now() + 1,
        text: "Lo siento, hubo un error al procesar tu mensaje. Por favor, inténtalo de nuevo.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  };

  const generateBotResponse = async (userMessage) => {
    const lowerMessage = userMessage.toLowerCase();
    
    // Respuestas específicas del usuario
    if (userData) {
      const userName = userData.nombreuser || userData.nombre || 'Usuario';
      
      // Saludo personalizado
      if (lowerMessage.includes('hola') || lowerMessage.includes('hi') || lowerMessage.includes('buenos')) {
        return `¡Hola ${userName}! 😊 Me da mucho gusto saludarte. ¿En qué puedo ayudarte hoy?`;
      }
      
      // Información sobre CIRP
      if (lowerMessage.includes('cirp') || lowerMessage.includes('que es') || lowerMessage.includes('sobre')) {
        return `CIRP es una plataforma educativa que te ayuda a crear y gestionar campañas de marketing digital. Puedes crear campañas de video y de audiencia, gestionar tus datos y mucho más. ¿Te gustaría saber más sobre alguna funcionalidad específica? 🚀`;
      }
      
      // Ayuda con campañas
      if (lowerMessage.includes('campaña') || lowerMessage.includes('campana')) {
        return `En CIRP puedes crear dos tipos de campañas:\n\n• **Campañas de Video**: Para promocionar productos o servicios con contenido multimedia\n• **Campañas de Audiencia**: Para generar leads y audiencias específicas\n\n¿Te gustaría que te explique cómo crear alguna de estas campañas? 📈`;
      }
      
      // Ayuda con módulos
      if (lowerMessage.includes('modulo') || lowerMessage.includes('curso') || lowerMessage.includes('aprender')) {
        return `Los módulos de CIRP contienen contenido educativo valioso. Puedes acceder a ellos desde el menú principal y dejar comentarios o preguntas. ¿Hay algún tema específico que te interese aprender? 📚`;
      }
      
      // Ayuda con datos
      if (lowerMessage.includes('datos') || lowerMessage.includes('informacion') || lowerMessage.includes('perfil')) {
        return `Puedes gestionar tus datos personales y ver el rendimiento de tus campañas en la sección "Mis Datos". Allí encontrarás estadísticas, enlaces de tus campañas y más información útil. ¿Necesitas ayuda con algo específico de tu perfil? 👤`;
      }
      
      // Ayuda técnica
      if (lowerMessage.includes('problema') || lowerMessage.includes('error') || lowerMessage.includes('no funciona')) {
        return `Entiendo que tienes un problema técnico. Te recomiendo:\n\n1. Verificar tu conexión a internet\n2. Recargar la página\n3. Limpiar la caché del navegador\n4. Contactar al soporte técnico\n\n¿Podrías describirme más específicamente qué problema estás experimentando? 🔧`;
      }
    }
    
    // Respuestas generales
    const responses = {
      'precio': 'Los precios de CIRP varían según el plan que elijas. Puedes revisar las opciones disponibles en la sección de membresías. ¿Te gustaría que te explique las diferencias entre los planes? 💰',
      'contacto': 'Puedes contactarnos a través de WhatsApp (disponible en la plataforma) o completar el formulario de contacto. También puedes usar este chat para resolver dudas rápidas. 📱',
      'ayuda': '¡Estoy aquí para ayudarte! 😊 Puedo asistirte con:\n\n• Creación de campañas\n• Gestión de datos\n• Navegación en la plataforma\n• Resolución de problemas técnicos\n\n¿En qué específicamente puedo ayudarte?',
      'gracias': '¡De nada! 😊 Es un placer poder ayudarte. ¿Hay algo más en lo que pueda asistirte?',
      'como': 'Te puedo ayudar a explicar cómo usar las diferentes funciones de CIRP. ¿Hay alguna funcionalidad específica que te gustaría aprender a usar? 🤔',
      'donde': 'Te puedo guiar para encontrar lo que buscas en CIRP. ¿Podrías ser más específico sobre qué estás buscando? 🔍'
    };
    
    // Buscar palabras clave en el mensaje
    for (const [keyword, response] of Object.entries(responses)) {
      if (lowerMessage.includes(keyword)) {
        return response;
      }
    }

    // Respuesta por defecto
    return `Gracias por tu mensaje. 😊 Estoy aquí para ayudarte con cualquier consulta sobre CIRP. Puedo asistirte con:\n\n• Creación y gestión de campañas\n• Navegación en la plataforma\n• Resolución de problemas\n• Información sobre funcionalidades\n\n¿Hay algo específico en lo que pueda ayudarte? 🤖`;
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      sendMessage();
      return false;
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const clearChat = () => {
    setMessages([{
      id: 1,
      text: "¡Hola! 👋 Soy tu asistente virtual de CIRP. ¿En qué puedo ayudarte hoy?",
      sender: 'bot',
      timestamp: new Date()
    }]);
  };

  return (
    <Sidebar>
      <div className="chat-ia-container">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={18}>
            <Card className="chat-ia-card">
              <div className="chat-ia-header">
                <div className="chat-ia-header-content">
                  <Avatar 
                    icon={<RobotOutlined />} 
                    size="large"
                    style={{ backgroundColor: '#6c757d' }}
                  />
                  <div className="chat-ia-header-text">
                    <Title level={4} style={{ margin: 0, color: '#333' }}>
                      Asistente Virtual CIRP
                    </Title>
                    <Text type="secondary">
                      En línea • Siempre disponible para ayudarte
                    </Text>
                  </div>
                </div>
                <Button
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={clearChat}
                  title="Limpiar conversación"
                />
              </div>

              {/* Mensajes */}
              <div className="chat-ia-messages-container">
                {isUserScrolling && (
                  <div className="chat-ia-scroll-to-bottom">
                    <Button
                      type="primary"
                      shape="circle"
                      icon={<FaIcons.FaArrowDown />}
                      onClick={() => {
                        setIsUserScrolling(false);
                        scrollToBottom();
                      }}
                      size="small"
                      style={{
                        backgroundColor: '#1976d2',
                        borderColor: '#1976d2',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                      }}
                    />
                  </div>
                )}
                <div className="chat-ia-messages" onScroll={handleScroll}>
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`chat-ia-message ${message.sender}`}
                    >
                      <div className="chat-ia-message-content">
                        <div className="chat-ia-message-avatar">
                        {message.sender === 'bot' ? (
                          <Avatar 
                            icon={<RobotOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#6c757d' }}
                          />
                        ) : (
                          <Avatar 
                            icon={<UserOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#1976d2' }}
                          />
                        )}
                        </div>
                        <div className="chat-ia-message-bubble">
                          <Text style={{ whiteSpace: 'pre-line' }}>{message.text}</Text>
                          <div className="chat-ia-message-time">
                            {formatTime(message.timestamp)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  {isLoading && (
                    <div className="chat-ia-message bot">
                      <div className="chat-ia-message-content">
                        <div className="chat-ia-message-avatar">
                          <Avatar 
                            icon={<RobotOutlined />} 
                            size="default"
                            style={{ backgroundColor: '#6c757d' }}
                          />
                        </div>
                        <div className="chat-ia-message-bubble">
                          <Spin size="small" />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </div>

              {/* Input del chatbot */}
              <div className="chat-ia-input">
                <TextArea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      e.stopPropagation();
                      sendMessage();
                    }
                  }}
                  placeholder="Escribe tu mensaje aquí..."
                  autoSize={{ minRows: 1, maxRows: 4 }}
                  style={{ 
                    resize: 'none',
                    border: '1px solid #d9d9d9',
                    borderRadius: '8px'
                  }}
                />
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={sendMessage}
                  disabled={!inputMessage.trim() || isLoading}
                  size="large"
                  style={{ 
                    backgroundColor: '#1976d2',
                    borderColor: '#1976d2',
                    borderRadius: '8px'
                  }}
                />
              </div>
            </Card>
          </Col>
          
          <Col xs={24} lg={6}>
            <Card title="💡 Consejos" className="chat-ia-tips">
              <div className="chat-ia-tips-content">
                <div className="tip-item">
                  <Text strong>🎯 Campañas</Text>
                  <Text type="secondary">Pregúntame sobre cómo crear campañas de video o audiencia</Text>
                </div>
                <div className="tip-item">
                  <Text strong>📊 Datos</Text>
                  <Text type="secondary">Te ayudo a entender tus estadísticas y métricas</Text>
                </div>
                <div className="tip-item">
                  <Text strong>🔧 Soporte</Text>
                  <Text type="secondary">Resuelvo problemas técnicos y dudas generales</Text>
                </div>
                <div className="tip-item">
                  <Text strong>📚 Aprendizaje</Text>
                  <Text type="secondary">Te guío en el uso de todas las funcionalidades</Text>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    </Sidebar>
  );
};

export default ChatIA;
